// Visitor Tracking Hook
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate or get visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('visitor_id', visitorId);
  }
  return visitorId;
};

// Get UTM parameters from URL
const getUtmParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || null,
    utm_medium: params.get('utm_medium') || null,
    utm_campaign: params.get('utm_campaign') || null,
  };
};

// Save UTM params to session storage (persist across page navigations)
const saveUtmParams = () => {
  const utmParams = getUtmParams();
  if (utmParams.utm_source) {
    sessionStorage.setItem('utm_source', utmParams.utm_source);
    sessionStorage.setItem('utm_medium', utmParams.utm_medium || '');
    sessionStorage.setItem('utm_campaign', utmParams.utm_campaign || '');
  }
};

// Get saved UTM params
const getSavedUtmParams = () => {
  return {
    utm_source: sessionStorage.getItem('utm_source') || getUtmParams().utm_source,
    utm_medium: sessionStorage.getItem('utm_medium') || getUtmParams().utm_medium,
    utm_campaign: sessionStorage.getItem('utm_campaign') || getUtmParams().utm_campaign,
  };
};

// Track event
const trackEvent = async (eventType, page) => {
  const visitorId = getVisitorId();
  const utmParams = getSavedUtmParams();
  
  try {
    await fetch(`${API_URL}/api/tracking/event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        visitor_id: visitorId,
        page: page,
        event_type: eventType,
        ...utmParams,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent,
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        timestamp: new Date().toISOString(),
      }),
    });
  } catch (error) {
    console.error('Tracking error:', error);
  }
};

// Main tracking hook
export const useVisitorTracking = () => {
  const location = useLocation();
  const heartbeatRef = useRef(null);
  const lastPageRef = useRef(null);

  useEffect(() => {
    // Save UTM params on first load
    saveUtmParams();

    // Track initial pageview
    const currentPage = location.pathname + location.search;
    if (currentPage !== lastPageRef.current) {
      trackEvent('pageview', currentPage);
      lastPageRef.current = currentPage;
    }

    // Start heartbeat (every 5 seconds)
    heartbeatRef.current = setInterval(() => {
      trackEvent('heartbeat', location.pathname);
    }, 5000);

    // Track when user leaves
    const handleBeforeUnload = () => {
      trackEvent('leave', location.pathname);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location]);
};

// Export for use in components
export { getVisitorId, getSavedUtmParams, trackEvent };
